import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { STAGING_MODE_OPTIONS } from '../../types/apiKeyConstants';

const CreateApiKeyDialog = ({
  open,
  onClose,
  suppliers,
  availableScopes,
  onConfirm,
  onCopyKey,
}) => {
  const [keyType, setKeyType] = useState('SUPPLIER_BOUND');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [label, setLabel] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [stagingMode, setStagingMode] = useState('NONE');
  const [newKeyResult, setNewKeyResult] = useState(null);

  const resetAndClose = () => {
    setNewKeyResult(null);
    onClose();
  };

  const handleOpen = () => {
    setNewKeyResult(null);
    setSelectedSupplier(null);
    setLabel('');
    setKeyType('SUPPLIER_BOUND');
    setSelectedScopes([]);
    setStagingMode('NONE');
  };

  // Reset when dialog opens
  React.useEffect(() => {
    if (open) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    const result = await onConfirm({
      keyType,
      selectedSupplier,
      label,
      selectedScopes,
      stagingMode,
    });
    if (result) setNewKeyResult(result);
  };

  const isCreateDisabled =
    keyType === 'SUPPLIER_BOUND'
      ? !selectedSupplier
      : selectedScopes.length === 0;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create New API Key</DialogTitle>
      <DialogContent>
        {!newKeyResult ? (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Select the key type and configure accordingly. The raw key is
              shown <b>only once</b>.
            </DialogContentText>
            <ToggleButtonGroup
              value={keyType}
              exclusive
              onChange={(_, v) => {
                if (v) setKeyType(v);
              }}
              sx={{ mb: 2 }}
              fullWidth
              size='small'
            >
              <ToggleButton value='SUPPLIER_BOUND'>Supplier-Bound</ToggleButton>
              <ToggleButton value='INTERNAL_UNRESTRICTED'>
                Internal
              </ToggleButton>
            </ToggleButtonGroup>
            {keyType === 'SUPPLIER_BOUND' && (
              <Autocomplete
                options={suppliers}
                getOptionLabel={(opt) =>
                  `${opt.supplierNo} – ${opt.supplierName}`
                }
                value={selectedSupplier}
                onChange={(_, v) => setSelectedSupplier(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Supplier'
                    required
                    margin='dense'
                  />
                )}
                sx={{ mb: 2 }}
              />
            )}
            {keyType === 'INTERNAL_UNRESTRICTED' && (
              <FormGroup sx={{ mb: 2 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Endpoint Scopes
                </Typography>
                {availableScopes.map((s) => (
                  <FormControlLabel
                    key={s.scope}
                    control={
                      <Checkbox
                        checked={selectedScopes.includes(s.scope)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedScopes([...selectedScopes, s.scope]);
                          } else {
                            setSelectedScopes(
                              selectedScopes.filter((sc) => sc !== s.scope)
                            );
                          }
                        }}
                      />
                    }
                    label={`${s.description} (${s.scope})`}
                  />
                ))}
              </FormGroup>
            )}
            <TextField
              label='Label (optional)'
              fullWidth
              margin='dense'
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <FormControl fullWidth margin='dense' sx={{ mt: 2 }}>
              <InputLabel>Staging Mode</InputLabel>
              <Select
                value={stagingMode}
                label='Staging Mode'
                onChange={(e) => setStagingMode(e.target.value)}
              >
                {STAGING_MODE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            <Alert severity='warning' sx={{ mb: 2 }}>
              Copy this key now — it will <b>not</b> be shown again.
            </Alert>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              <Typography
                variant='body2'
                fontFamily='monospace'
                sx={{ flexGrow: 1 }}
              >
                {newKeyResult.rawKey}
              </Typography>
              <IconButton
                size='small'
                onClick={() => onCopyKey(newKeyResult.rawKey)}
              >
                <ContentCopyIcon fontSize='small' />
              </IconButton>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!newKeyResult ? (
          <>
            <Button onClick={resetAndClose}>Cancel</Button>
            <Button
              variant='contained'
              onClick={handleConfirm}
              disabled={isCreateDisabled}
            >
              Create
            </Button>
          </>
        ) : (
          <Button variant='contained' onClick={resetAndClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateApiKeyDialog;
